// ============================================
// Shared marketing CTA pieces (G2M batch 1)
//
// One component family renders the phase-appropriate primary CTA on
// every funnel page (plan finding F3). Pre-approval (Phase A) the
// primary action is email capture with lead-magnet delivery; once
// APP_STORE_URL is set in marketingConfig.ts (Day 0) every page
// flips to an App Store download without touching page code.
//
// CRO pass (funnel-page-cro): added a compact inline capture used in
// the hero and at the calculator result, a mobile sticky CTA bar, a
// source-citation trust strip mirroring the homepage, and a partner
// context banner. All reuse the existing waitlist plumbing and the
// existing analytics event names.
// ============================================

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { submitWaitlistEmail } from '../../../data/cloud';
import { trackEvent } from '../../../utils/analytics';
import { AppStoreBadge } from '../LandingPage';
import {
  APP_STORE_URL,
  APP_STORE_FALLBACK,
  appStoreLive,
  getRawTrafficSource,
  getTrafficSource,
} from './marketingConfig';

// Shared email format check (mirrors the landing waitlist form).
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// ---- App Store badge that respects the Day-0 flip ----
export const FunnelAppStoreBadge: React.FC<{ size?: 'sm' | 'lg'; page: string }> = ({ size = 'lg', page }) => {
  const navigate = useNavigate();
  const onClick = useCallback(() => {
    void trackEvent('marketing_appstore_click', { page, live: appStoreLive() });
    if (APP_STORE_URL) {
      window.location.href = APP_STORE_URL;
    } else {
      navigate(APP_STORE_FALLBACK);
    }
  }, [navigate, page]);
  return <AppStoreBadge size={size} onClick={onClick} />;
};

// ---- Email capture with magnet delivery ----
export type MagnetInfo = {
  /** Visible name, e.g. "Reconstitution Cheat Sheet" */
  name: string;
  /** Path under /downloads, e.g. "/downloads/reconstitution-cheat-sheet.pdf" */
  href: string;
};

/** One phase-aware next step shown after a successful capture (finding F6).
    Pre-approval: a free-content link the visitor can act on while they wait.
    Post-approval: the real App Store badge. */
export type NextStep = { label: string; to: string };

const CaptureNextStep: React.FC<{ page: string; nextStep?: NextStep }> = ({ page, nextStep }) => {
  if (appStoreLive()) {
    return (
      <span className="fp-capture-next">
        <FunnelAppStoreBadge size="sm" page={page} />
      </span>
    );
  }
  if (!nextStep) return null;
  return (
    <span className="fp-capture-next">
      <Link className="fp-capture-next-link" to={nextStep.to}>{nextStep.label}</Link>
    </span>
  );
};

type EmailCaptureProps = {
  page: string;
  headline: string;
  lede: string;
  buttonLabel?: string;
  magnet?: MagnetInfo;
  /** Phase-aware next step surfaced on success (finding F6). */
  nextStep?: NextStep;
  /** Optional id so the sticky CTA / hero button can target this form. */
  id?: string;
  /** Where on the page this capture sits, for waitlist_signup attribution.
      This is the canonical bottom-of-page form, so it defaults to 'bottom'. */
  placement?: string;
};

export const FunnelEmailCapture: React.FC<EmailCaptureProps> = ({ page, headline, lede, buttonLabel = 'Send it to me', magnet, nextStep, id, placement = 'bottom' }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [error, setError] = useState('');

  const onSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || status === 'submitting' || status === 'success') return;
    // The form is noValidate (to keep the styled error path), so validate here.
    if (!EMAIL_RE.test(trimmed)) {
      setStatus('error');
      setError('That email does not look right. Check it and try again.');
      return;
    }
    setStatus('submitting');
    setError('');
    const source = getTrafficSource(page);
    const result = await submitWaitlistEmail(trimmed, source);
    if (result.success) {
      setStatus('success');
      if (!result.duplicate) {
        void trackEvent('waitlist_signup', { page, source, magnet: magnet?.name ?? null, placement });
      }
    } else {
      setStatus('error');
      setError(result.error || 'Something went wrong. Please try again.');
    }
  }, [email, status, page, magnet, placement]);

  return (
    <section className="lp-email-capture" aria-label={headline} id={id}>
      <div className="lp-email-capture-inner">
        <div className="lp-section-head lp-section-head--center">
          <span className="lp-section-head__eyebrow">{magnet ? 'Free download' : 'Stay in the loop'}</span>
          <h2 className="lp-section-head__title">{headline}</h2>
          <p className="lp-section-head__lede">{lede}</p>
        </div>
        {status === 'success' ? (
          <div className="lp-email-capture-success" role="status">
            <p className="fp-capture-success-line">
              {magnet ? (
                <>
                  Thanks. Your copy is ready:{' '}
                  <a
                    href={magnet.href}
                    download
                    onClick={() => void trackEvent('magnet_downloaded', { page, magnet: magnet.name })}
                  >
                    download the {magnet.name}
                  </a>
                  . Save it somewhere handy
                  {appStoreLive() ? '. PRTCL is on the App Store now.' : ', and we will email you as soon as the app is available to download.'}
                </>
              ) : appStoreLive() ? (
                'Thanks. PRTCL is on the App Store now.'
              ) : (
                'Thanks. We will email you as soon as the app is available to download.'
              )}
            </p>
            <CaptureNextStep page={page} nextStep={nextStep} />
          </div>
        ) : (
          <>
            <form className="lp-email-capture-form" onSubmit={onSubmit} noValidate>
              <input
                type="email"
                required
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={status === 'submitting'}
                className="lp-email-capture-input"
                aria-label="Email address"
              />
              <button
                type="submit"
                className="lp-btn-primary lp-email-capture-button"
                disabled={!email || status === 'submitting'}
              >
                {status === 'submitting' ? 'Sending...' : buttonLabel}
              </button>
            </form>
            {status === 'error' && (
              <p className="lp-email-capture-error" role="alert">{error}</p>
            )}
          </>
        )}
      </div>
    </section>
  );
};

// ---- Compact inline capture (hero + calculator result) ----
// Single-field, low-friction. Reuses the same waitlist plumbing and the
// same analytics event names as the section capture above. A distinct
// `sourceKey` lets the calculator-result variant attribute separately.
type InlineCaptureProps = {
  page: string;
  buttonLabel: string;
  ariaLabel: string;
  placeholder?: string;
  magnet?: MagnetInfo;
  /** Attribution key passed to getTrafficSource (defaults to `page`). */
  sourceKey?: string;
  /** Where on the page this capture sits, for waitlist_signup attribution
      (e.g. 'hero', 'calculator_result'). Lets us measure which placement
      converts without renaming the shared waitlist_signup event. */
  placement: string;
  /** Confirmation line shown before the magnet link on success. */
  successLead?: string;
  /** Phase-aware next step surfaced on success. */
  nextStep?: NextStep;
  className?: string;
};

export const FunnelInlineCapture: React.FC<InlineCaptureProps> = ({
  page,
  buttonLabel,
  ariaLabel,
  placeholder = 'you@example.com',
  magnet,
  sourceKey,
  placement,
  successLead,
  nextStep,
  className,
}) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [error, setError] = useState('');

  const onSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || status === 'submitting' || status === 'success') return;
    if (!EMAIL_RE.test(trimmed)) {
      setStatus('error');
      setError('That email does not look right. Check it and try again.');
      return;
    }
    setStatus('submitting');
    setError('');
    const source = getTrafficSource(sourceKey ?? page);
    const result = await submitWaitlistEmail(trimmed, source);
    if (result.success) {
      setStatus('success');
      if (!result.duplicate) {
        void trackEvent('waitlist_signup', { page, source, magnet: magnet?.name ?? null, placement });
      }
    } else {
      setStatus('error');
      setError(result.error || 'Something went wrong. Please try again.');
    }
  }, [email, status, page, sourceKey, magnet, placement]);

  return (
    <div className={`fp-inline-capture ${className ?? ''}`}>
      {status === 'success' ? (
        <div className="fp-inline-capture-success" role="status">
          <p className="fp-capture-success-line">
            {successLead ?? 'Thanks, it is on the way.'}
            {magnet && (
              <>
                {' '}
                <a
                  href={magnet.href}
                  download
                  onClick={() => void trackEvent('magnet_downloaded', { page, magnet: magnet.name })}
                >
                  Download the {magnet.name}
                </a>
                .
              </>
            )}
          </p>
          <CaptureNextStep page={page} nextStep={nextStep} />
        </div>
      ) : (
        <>
          <form className="fp-inline-form" onSubmit={onSubmit} noValidate aria-label={ariaLabel}>
            <input
              type="email"
              required
              placeholder={placeholder}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={status === 'submitting'}
              className="lp-email-capture-input"
              aria-label="Email address"
            />
            <button
              type="submit"
              className="lp-btn-primary lp-email-capture-button"
              disabled={!email || status === 'submitting'}
            >
              {status === 'submitting' ? 'Sending...' : buttonLabel}
            </button>
          </form>
          {status === 'error' && (
            <p className="fp-inline-error" role="alert">{error}</p>
          )}
        </>
      )}
    </div>
  );
};

// ---- Sticky mobile CTA bar ----
// Appears after ~50% scroll on phones and slides away when the target
// form scrolls into view (so it never covers the form). Pre-approval it
// scrolls to + focuses the target form; post-approval it is a real App
// Store CTA. Desktop hides it entirely via CSS.
export const FunnelStickyCta: React.FC<{ page: string; label: string; targetId: string }> = ({ page, label, targetId }) => {
  const [visible, setVisible] = useState(false);
  const live = appStoreLive();

  useEffect(() => {
    let pastHalf = false;
    let targetInView = false;
    const apply = () => setVisible(pastHalf && !targetInView);

    const onScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      pastHalf = scrollable > 0 ? window.scrollY / scrollable >= 0.5 : false;
      apply();
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    let observer: IntersectionObserver | undefined;
    const target = document.getElementById(targetId);
    if (target && typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(
        (entries) => {
          targetInView = entries.some((entry) => entry.isIntersecting);
          apply();
        },
        { threshold: 0 },
      );
      observer.observe(target);
    }

    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      observer?.disconnect();
    };
  }, [targetId]);

  const onClick = useCallback(() => {
    if (live && APP_STORE_URL) {
      void trackEvent('marketing_appstore_click', { page, live: true });
      window.location.href = APP_STORE_URL;
      return;
    }
    const target = document.getElementById(targetId);
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const input = target.querySelector('input');
    if (input) {
      // Wait for the smooth scroll before pulling focus so the field is in view.
      window.setTimeout(() => (input as HTMLInputElement).focus(), 450);
    }
  }, [live, page, targetId]);

  return (
    <div className={`fp-sticky-cta ${visible ? 'is-visible' : ''}`} aria-hidden={!visible}>
      <button
        type="button"
        className="lp-btn-primary fp-sticky-cta-btn"
        onClick={onClick}
        tabIndex={visible ? 0 : -1}
      >
        {live ? 'Get PRTCL on the App Store' : label}
      </button>
    </div>
  );
};

// ---- Source-citation trust strip (mirrors the homepage strip) ----
// App Review 1.4.1: the old strip claimed a kind of clinician sourcing the
// product does not have. It now says the true thing, matching LandingPage's
// TRUST_PILLS: every claim is cited to a published source, and PRTCL never
// picks the dose. The pills avoid the three-letter agency acronym, which the
// copy scanner bans in funnel copy. Classes live in LandingPage.css
// (already imported by every funnel page).
const FUNNEL_TRUST_PILLS = ['Official Drug Labels', 'Peer-Reviewed Research', 'Every Claim Cited'] as const;

export const FunnelTrustStrip: React.FC = () => (
  <section className="lp-trust-strip" aria-label="Every claim cited to a published source">
    <div className="lp-trust-strip-inner">
      <span className="lp-eyebrow">Every claim cited to a published source</span>
      <p className="lp-trust-strip-lede">
        Every health statement in PRTCL links to where it came from: official prescribing
        information, peer-reviewed research, or published clinical guidance. Anything that
        could not be cited was left out. And PRTCL never tells you what dose to take. That
        part stays between you and your doctor.
      </p>
      <ul className="lp-trust-pills" role="list">
        {FUNNEL_TRUST_PILLS.map((pill) => (
          <li key={pill} className="lp-trust-pill">{pill}</li>
        ))}
      </ul>
    </div>
  </section>
);

// ---- Partner context banner (finding: partner-sourced arrivals) ----
// Visitors whose stored traffic source starts with "partner" (a partner QR
// / insert card) get a one-line orienting header. Client-only: the stored
// source is read post-mount, so this never appears in the prerendered HTML.
export const FunnelPartnerBanner: React.FC = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const raw = getRawTrafficSource();
    if (raw && raw.toLowerCase().startsWith('partner')) setShow(true);
  }, []);
  if (!show) return null;
  return (
    <div className="fp-partner-banner" role="note">
      <span>Your peptide just arrived. Here is exactly what to do next.</span>
    </div>
  );
};

// ---- Slim nav + footer shared by funnel pages ----
export const FunnelNav: React.FC<{ page: string }> = ({ page }) => (
  <nav className="lp-nav">
    <div className="lp-nav-inner">
      <a className="lp-wordmark" href="/">PRTCL<span className="lp-wordmark__dot">.</span></a>
      <div className="lp-nav-links">
        <Link className="lp-nav-link" to="/learn">Peptides 101</Link>
        <Link className="lp-nav-link" to="/tools/reconstitution-calculator">Calculator</Link>
        {/* Honest label before approval: the badge routed to the beta, not the store. */}
        {appStoreLive() ? (
          <FunnelAppStoreBadge size="sm" page={page} />
        ) : (
          <Link className="fp-nav-beta" to={APP_STORE_FALLBACK}>Join the beta</Link>
        )}
      </div>
    </div>
  </nav>
);

export const FunnelFooter: React.FC = () => (
  <>
    <div className="lp-footer-top">
      <div className="lp-footer-top-inner">
        <p className="lp-disclaimer">
          PRTCL does not provide medical advice, diagnosis, or treatment. Information on
          this page and in the app is for educational purposes only. Always consult your
          healthcare provider before starting any peptide protocol.
        </p>
      </div>
    </div>
    <footer className="lp-footer">
      <div className="lp-footer-inner">
        <div className="lp-footer__wordmark">PRTCL<span className="lp-wordmark__dot">.</span></div>
        <div className="lp-footer__links">
          <Link to="/privacy">Privacy</Link>
          <Link to="/consumer-health-data">Consumer Health Data</Link>
          <Link to="/terms">Terms</Link>
        </div>
        <span className="lp-footer__copy">© {new Date().getFullYear()} PRTCL</span>
      </div>
    </footer>
  </>
);
